import React from 'react';
import { Avatar, Typography } from 'antd';
import { TYPING_MESSAGES } from '../../../constants/typing-messages';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';
import { Bot } from 'lucide-react';

const { Text } = Typography;

interface AITypingIndicatorProps {
  show: boolean;
}

export const AITypingIndicator: React.FC<AITypingIndicatorProps> = ({ show }) => {
  const { fontSize } = useResponsiveSizes();
  const messageIndexRef = React.useRef(0);
  const [displayedMessage, setDisplayedMessage] = React.useState(TYPING_MESSAGES[0]);

  React.useEffect(() => {
    if (!show) {
      messageIndexRef.current = 0;
      setDisplayedMessage(TYPING_MESSAGES[0]);
      return;
    }

    // Cambiar mensaje cada x segundos
    const interval = setInterval(() => {
      messageIndexRef.current = (messageIndexRef.current + 1) % TYPING_MESSAGES.length;
      setDisplayedMessage(TYPING_MESSAGES[messageIndexRef.current]);
    }, 8000);

    return () => clearInterval(interval);
  }, [show]);

  const dotsStyles: React.CSSProperties = {
    width: '2px',
    height: '2px',
    borderRadius: '50%',
    backgroundColor: '#999'
  };

  if (!show) return null;

  return (
    <div
      style={{
        maxWidth: '70%',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'baseline',
        borderRadius: 16,
        padding: '10px 14px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
        <Avatar
          size={28}
          icon={<Bot width={15} />}
          style={{
            backgroundColor: '#4a78dc',
            marginRight: 8,
            color: 'white',
            animation: 'aiPulse 1.5s ease-in-out infinite'
          }}
        />
        <Text style={{ fontSize: fontSize.base, fontWeight: 500, color: '#333' }}>{'DRUPI'}</Text>
      </div>

      <div style={{ display: 'flex', gap: '2px', alignItems: 'baseline' }}>
        <Text
          style={{
            fontSize: fontSize.base,
            color: '#999',
            fontStyle: 'italic',
            marginRight: 8,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          {displayedMessage}
        </Text>
        <span
          style={{
            ...dotsStyles,
            animation: 'typingDot1 1.4s infinite ease-in-out'
          }}
        />
        <span
          style={{
            ...dotsStyles,
            animation: 'typingDot2 1.4s infinite ease-in-out'
          }}
        />
        <span
          style={{
            ...dotsStyles,
            animation: 'typingDot3 1.4s infinite ease-in-out'
          }}
        />
      </div>

      <style>{`
        @keyframes aiPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.7);
          }
          50% {
            transform: scale(1.03);
            box-shadow: 0 0 0 8px rgba(24, 144, 255, 0);
          }
        }

        @keyframes typingDot1 {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }

        @keyframes typingDot2 {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }

        @keyframes typingDot3 {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }

        @keyframes typingDot2 { animation-delay: 0.2s; }
        @keyframes typingDot3 { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};
